import { useEffect, useState, useRef } from "react";
import * as React from "react";
import { Button, Form, FormInstance } from "antd";

import DataSelection from "./DataSelection";
import BaseCurrencyForm from "./BaseCurrencyForm";
import { BaseCurrencySelectionType, BaseCurrencyValue, DateSelectionType, DateValue, QuoteCurrencySelectionType, QuoteCurrencyValue, CurrencyPlaceholderName } from "../../../types/newLinkBuilder";
import QuoteCurrencyForm from "./QuoteCurrency";
import DateForm from "./DateForm";
import FormulaBuilderForm from "../FormulaBuilderForm";
import { DialogMessageType } from "../../../types/commands";
import { validateForm } from "../../../utils/validation";
import { buildFormula, FormulaType } from "../../../utils/formulaBuilder";
import { useStyles } from "../../style";
import useFetchCurrencies from "../../../utils/useFetchCurrencies";

const NewLinkBuilderForm: React.FC = () => {
    const styles = useStyles();
    const [form] = Form.useForm();
    const { currencyOptions, fetchCurrencies } = useFetchCurrencies();

    useEffect(() => {
        fetchCurrencies();
    }, []);

    const baseCurrencyRef = useRef<HTMLDivElement>(null);
    const quoteCurrencyRef = useRef<HTMLDivElement>(null);
    const dateRef = useRef<HTMLDivElement>(null);
    const formulaBuilderRef = useRef<HTMLDivElement>(null);
    const [scrollTarget, setScrollTarget] = useState<'baseCurrency' | 'quoteCurrency' | 'date' | 'formula' | null>(null);

    const [selectedDataPoint, setSelectedDataPoint] = useState<string>("Daily_Closing_Value");

    const [baseCurrency, setBaseCurrency] = useState<BaseCurrencyValue>({
        selected: BaseCurrencySelectionType.Value,
        currency: "",
        currencyCellReference: "",
    })

    const [quoteCurrency, setQuoteCurrency] = useState<QuoteCurrencyValue>({
        selected: QuoteCurrencySelectionType.Value,
        currency: "",
        currencyCellReference: "",
    })

    const [date, setDate] = useState<DateValue>({
        selected: DateSelectionType.Date,
        date: "",
        dateCellReference: "",
    })

    const handlePlaceholder = (name: CurrencyPlaceholderName) => {
        const value = {
            placeholderName: name,
            data: {
                selectedDataPoint,
                baseCurrency,
                quoteCurrency,
                date,
                formulaLocation,
            }
        }

        localStorage.setItem("placeholder", JSON.stringify(value));
        localStorage.setItem("redirectTo", "currency-data");
        Office.context.ui.messageParent(JSON.stringify({
            action: DialogMessageType.OPEN_PLACEHOLDER,
        }));

    }

    const [formula, setFormula] = useState<string>("");
    const [formulaLocation, setFormulaLocation] = useState<string>("");

    const handleOk = (form: FormInstance) => {
        const selectedDataPointError = validateForm(selectedDataPoint, form, "dataSelection");
        const baseCurrencyError = validateForm(baseCurrency, form, "baseCurrency");
        const quoteCurrencyError = validateForm(quoteCurrency, form, "quoteCurrency");
        const dateError = validateForm(date, form, "date");
        const formulaLocationError = validateForm(formulaLocation, form, "formulaLocation");

        const hasError = selectedDataPointError || baseCurrencyError || quoteCurrencyError || dateError || formulaLocationError;
        if (!hasError) {
            Office.context.ui.messageParent(JSON.stringify({
                action: DialogMessageType.ADD_LINK_TO_CELL,
                data: {
                    formula: formula,
                    address: formulaLocation,
                },
            }));
        }
    }

    const handleCancel = () => {
        localStorage.removeItem("placeholder");
        localStorage.removeItem("cellAddress");
        Office.context.ui.messageParent(JSON.stringify({
            action: DialogMessageType.CLOSE,
        }));
    }

    useEffect(() => {
        const buildFormulaAsync = async () => {
            const formula = await buildFormula(
                FormulaType.Currency,
                {
                    selectedDataPoint,
                    baseCurrency,
                    date,
                    quoteCurrency
                }
            );
            setFormula(formula);
        }

        buildFormulaAsync()
    }, [selectedDataPoint, baseCurrency, date, quoteCurrency, formulaLocation]);

    useEffect(() => {
        if (scrollTarget === 'baseCurrency') {
            baseCurrencyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        } else if (scrollTarget === 'quoteCurrency') {
            quoteCurrencyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        } else if (scrollTarget === 'date') {
            dateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        } else if (scrollTarget === 'formula') {
            formulaBuilderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        }
    }, [scrollTarget]);

    useEffect(() => {
        const placeholder = localStorage.getItem("placeholder");
        const cellAddress = localStorage.getItem("cellAddress");
        const activeCell = localStorage.getItem("activeCell");
        if (activeCell) {
            setFormulaLocation(activeCell);

        }
        if (placeholder) {
            const placeholderData = JSON.parse(placeholder);

            setSelectedDataPoint(placeholderData.data.selectedDataPoint);
            setBaseCurrency(placeholderData.data.baseCurrency);
            setQuoteCurrency(placeholderData.data.quoteCurrency);
            setDate(placeholderData.data.date);
            setFormulaLocation(placeholderData.data.formulaLocation);

            switch (placeholderData.placeholderName) {
                case CurrencyPlaceholderName.BaseCurrencyCellReference:
                    setBaseCurrency(prev => ({
                        ...prev,
                        selected: BaseCurrencySelectionType.CellReference,
                        currencyCellReference: cellAddress,
                    }));
                    setScrollTarget('baseCurrency');
                    break;
                case CurrencyPlaceholderName.QuoteCurrencyCellReference:
                    setQuoteCurrency(prev => ({
                        ...prev,
                        selected: QuoteCurrencySelectionType.CellReference,
                        currencyCellReference: cellAddress,
                    }));
                    setScrollTarget('quoteCurrency');
                    break;
                case CurrencyPlaceholderName.DateCellReference:
                    setDate(prev => ({
                        ...prev,
                        selected: DateSelectionType.CellReference,
                        dateCellReference: cellAddress,
                    }));
                    setScrollTarget('date');
                    break;
                case CurrencyPlaceholderName.FormulaLocationCellReference:
                    setFormulaLocation(cellAddress);
                    setScrollTarget('formula');
                    break;
            }
        }

        return () => {
            localStorage.removeItem("placeholder");
            localStorage.removeItem("cellAddress");
        }
    }, []);

    useEffect(() => {
        form.setFieldsValue({
            baseCurrency: baseCurrency.selected,
            quoteCurrency: quoteCurrency.selected,
            date: date.selected
        });
    }, [baseCurrency.selected, quoteCurrency.selected, date.selected]);

    return (
        <Form form={form}
            initialValues={{
                baseCurrency: BaseCurrencySelectionType.Value,
                quoteCurrency: QuoteCurrencySelectionType.Value,
                date: DateSelectionType.Date
            }}
            onValuesChange={(changedValues) => {
                if (changedValues.baseCurrency !== undefined) {
                    setBaseCurrency(prev => ({ ...prev, selected: changedValues.baseCurrency }));
                    if (changedValues.baseCurrency === BaseCurrencySelectionType.Value) {
                        setBaseCurrency(prev => ({ ...prev, currencyCellReference: "" }));
                    } else {
                        setBaseCurrency(prev => ({ ...prev, currency: "" }));
                    }
                }
                if (changedValues.quoteCurrency !== undefined) {
                    setQuoteCurrency(prev => ({ ...prev, selected: changedValues.quoteCurrency }));
                    if (changedValues.quoteCurrency === QuoteCurrencySelectionType.Value) {
                        setQuoteCurrency(prev => ({ ...prev, currencyCellReference: "" }));
                    } else {
                        setQuoteCurrency(prev => ({ ...prev, currency: "" }));
                    }
                }
                if (changedValues.date !== undefined) {
                    setDate(prev => ({ ...prev, selected: changedValues.date }));

                    if (changedValues.date === DateSelectionType.Date) {
                        setDate(prev => ({ ...prev, dateCellReference: "" }));
                    } else {
                        setDate(prev => ({ ...prev, date: "" }));
                    }
                }
            }}
            className={styles.radio}
        >
            <DataSelection selectedDataPoint={selectedDataPoint} setSelectedDataPoint={setSelectedDataPoint} />
            <div ref={baseCurrencyRef}>
                <BaseCurrencyForm baseCurrencyValue={baseCurrency} setBaseCurrencyValue={setBaseCurrency} currencyOptions={currencyOptions} handlePlaceholder={handlePlaceholder} />
            </div>
            <div ref={quoteCurrencyRef}>
                <QuoteCurrencyForm quoteCurrencyValue={quoteCurrency} setQuoteCurrencyValue={setQuoteCurrency} currencyOptions={currencyOptions} handlePlaceholder={handlePlaceholder} />
            </div>
            <div ref={dateRef}>
                <DateForm dateValue={date} setDateValue={setDate} handlePlaceholder={handlePlaceholder} />
            </div>
            <div ref={formulaBuilderRef}>
                <FormulaBuilderForm formula={formula} formulaLocation={formulaLocation} setFormulaLocation={setFormulaLocation} handlePlaceholder={handlePlaceholder} />
            </div>

            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '10px',
                marginBlock: '20px',
            }}>
                <Button type="primary" onClick={() => handleOk(form)}>Ok</Button>
                <Button onClick={handleCancel}>Cancel</Button>
            </div>
        </Form >
    );
};

export default NewLinkBuilderForm;
